import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// Browser audio recorder. Returns Blob via onComplete.
// `floating`: renders just the mic button as a corner FAB (anchored to the
// app frame, same convention as the Hifz page's other floating buttons)
// instead of the centered button + waveform + caption block, so it never
// sits on top of the Mushaf/hidden-ayah content above it.
export default function Recorder({ onComplete, disabled, testid = "recorder", floating = false }) {
  const { t: tr } = useLanguage();
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState(null);
  const [level, setLevel] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const startedAtRef = useRef(null);
  const streamRef = useRef(null);
  const unmountedRef = useRef(false);

  const start = async () => {
    setError(null);
    setElapsed(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // The user left the page while the mic-permission prompt was still open.
      if (unmountedRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4"
        : "";
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      const usedMime = mr.mimeType || mimeType || "audio/webm";
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data && e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: usedMime });
        const durationSeconds = startedAtRef.current ? (Date.now() - startedAtRef.current) / 1000 : 0;
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        setLevel(0);
        setElapsed(durationSeconds);
        setRecording(false);
        if (blob.size < 500) {
          setError(tr("recorder.noAudioCaptured"));
          return;
        }
        onComplete?.(blob, durationSeconds);
      };
      mediaRecorderRef.current = mr;
      startedAtRef.current = Date.now();
      // Timeslice flushes chunks every 250ms — more reliable than waiting for stop().
      mr.start(250);
      // Setup analyser for waveform
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setLevel(avg / 255);
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
      setRecording(true);
    } catch (e) {
      setError(tr("recorder.micAccessDenied"));
    }
  };

  const stop = () => {
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    if (mr.state === "inactive") return;
    try { mr.requestData && mr.requestData(); } catch {}
    try { mr.stop(); } catch {}
    // onstop handler will flip recording state + call onComplete
  };

  // Live "0:06" counter while recording; the final length stays shown after stopping.
  useEffect(() => {
    if (!recording) return undefined;
    const id = setInterval(() => setElapsed((Date.now() - startedAtRef.current) / 1000), 250);
    return () => clearInterval(id);
  }, [recording]);

  // Reads refs only (never the `recording` state — an empty-deps cleanup would
  // see its stale initial `false`). The reset in the setup half keeps this
  // correct under StrictMode's mount → unmount → mount dev cycle.
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      const mr = mediaRecorderRef.current;
      if (mr) {
        mr.onstop = null; // abandoned take: don't fire onComplete (it would upload + score it after the user has left)
        if (mr.state !== "inactive") { try { mr.stop(); } catch {} }
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (floating) {
    return (
      <div data-testid={testid}>
        <div className="absolute bottom-[calc(var(--mobile-tabbar-h)_+_env(safe-area-inset-bottom)_+_1rem)] right-6 z-40">
          <button
            data-testid={recording ? "stop-recording-btn" : "start-recording-btn"}
            onClick={recording ? stop : start}
            disabled={disabled}
            type="button"
            title={recording ? tr("recorder.recordingTapToStop") : tr("recorder.tapToStartReciting")}
            aria-label={recording ? tr("recorder.recordingTapToStop") : tr("recorder.tapToStartReciting")}
            className={`relative z-10 h-12 w-12 rounded-full grid place-items-center shadow-lg transition-all duration-300 ${
              recording
                ? "bg-terracotta-500 text-white"
                : "bg-forest-700 text-sand-100 hover:bg-forest-600"
            } disabled:opacity-50`}
          >
            {disabled ? <Loader2 className="h-5 w-5 animate-spin" />
              : recording ? <Square className="h-4 w-4" /> : <Mic className="h-5 w-5" />}
          </button>
          {recording && (
            <div className="absolute inset-0 rounded-full border-2 border-terracotta-500 animate-ping opacity-60 pointer-events-none" />
          )}
        </div>
        {recording && (
          <div className="absolute bottom-[calc(var(--mobile-tabbar-h)_+_env(safe-area-inset-bottom)_+_4.5rem)] right-6 z-40 rounded-full bg-terracotta-500 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-white" data-testid="recorder-timer">
            {fmtTime(elapsed)}
          </div>
        )}
        {error && (
          <p className="absolute bottom-[calc(var(--mobile-tabbar-h)_+_env(safe-area-inset-bottom)_+_5.5rem)] right-6 z-40 max-w-[60%] text-right text-xs text-terracotta-500" data-testid="recorder-error">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div data-testid={testid} className="flex flex-col items-center gap-5">
      <div className="relative">
        <button
          data-testid={recording ? "stop-recording-btn" : "start-recording-btn"}
          onClick={recording ? stop : start}
          disabled={disabled}
          type="button"
          className={`relative z-10 h-24 w-24 rounded-full grid place-items-center transition-all duration-300 ${
            recording
              ? "bg-terracotta-500 text-white"
              : "bg-forest-600 text-sand-100 hover:bg-forest-700"
          } disabled:opacity-50`}
        >
          {disabled ? <Loader2 className="h-9 w-9 animate-spin" />
            : recording ? <Square className="h-8 w-8" /> : <Mic className="h-9 w-9" />}
        </button>
        {recording && (
          <div className="absolute inset-0 rounded-full border-2 border-terracotta-500 animate-ping opacity-60 pointer-events-none" />
        )}
      </div>
      {/* Waveform bars */}
      <div className="flex items-end gap-1.5 h-12">
        {Array.from({ length: 24 }).map((_, i) => {
          const variance = Math.sin((Date.now() / 200) + i) * 0.3 + 0.7;
          const h = recording ? Math.max(8, Math.min(48, level * 60 * variance + Math.random() * 8)) : 4;
          return (
            <div
              key={i}
              className="w-1.5 rounded-full bg-forest-600/70"
              style={{ height: `${h}px`, transition: "height 80ms ease-out" }}
            />
          );
        })}
      </div>
      <p className="text-sm text-muted-foreground" data-testid="recorder-caption">
        {recording
          ? <>{tr("recorder.recording")} <span className="font-semibold tabular-nums text-terracotta-500" data-testid="recorder-timer">{fmtTime(elapsed)}</span> {tr("recorder.reciteTheAyahClearly")}</>
          : elapsed > 0
            ? <>{tr("recorder.recorded")} <span className="font-semibold tabular-nums" data-testid="recorder-timer">{fmtTime(elapsed)}</span> {tr("recorder.tapTheMicToRecord")}</>
            : tr("recorder.tapTheMicToBegin")}
      </p>
      {error && <p className="text-sm text-terracotta-500" data-testid="recorder-error">{error}</p>}
    </div>
  );
}
